import { gridQueryKey } from '@/shared/hooks';
import { baseGridDefaults } from './gridDefaults';
import type { GridQueryInput } from './gridUtils';
import type { GridPath } from '@/shared/hooks';
import type { QueryResult } from '@flowqa/shared';
import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid';

export type GridConfig<TDto> = {
  queryKey: ReturnType<typeof gridQueryKey>;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<TDto>>;
  pageSizeOptions: number[];
  noRowsText: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  persistKey: string | null;
  syncStateToUrl: boolean;
};

export type GridConfigFactoryOptions<TDto> = {
  path: GridPath;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<TDto>>;
  noRowsText: string;
  persistKey: string | null;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  pageSizeOptions?: number[];
  syncStateToUrl?: boolean;
};

export type EntityGridConfigOptions<TDto> = Omit<GridConfigFactoryOptions<TDto>, 'persistKey'> & {
  defaultPersistKey: string;
  persistKey?: string | null;
};

type FeatureGridBaseOptions<TDto> = {
  path: GridPath;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<TDto>>;
  noRowsText: string;
  defaultPersistKey: string;
  defaultSortModel: GridSortModel;
};

type FeatureGridOverrides = {
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  fixedFilters?: GridQueryInput['filters'];
};

export const createGridConfig = <TDto>({
  path,
  queryFn,
  noRowsText,
  persistKey,
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  pageSizeOptions,
  syncStateToUrl = false,
}: GridConfigFactoryOptions<TDto>): GridConfig<TDto> => ({
  queryKey: gridQueryKey(path),
  queryFn,
  pageSizeOptions: pageSizeOptions ?? baseGridDefaults.pageSizeOptions,
  noRowsText,
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  persistKey,
  syncStateToUrl,
});

export const createEntityGridConfig = <TDto>({
  defaultPersistKey,
  persistKey,
  ...options
}: EntityGridConfigOptions<TDto>): GridConfig<TDto> =>
  createGridConfig<TDto>({ ...options, persistKey: persistKey ?? defaultPersistKey });

export const createFeatureGridConfig = <TDto>(
  base: FeatureGridBaseOptions<TDto>,
  overrides: FeatureGridOverrides = {},
): GridConfig<TDto> =>
  createEntityGridConfig<TDto>({
    path: base.path,
    queryFn: base.queryFn,
    noRowsText: base.noRowsText,
    fixedFilters: overrides.fixedFilters,
    initialSortModel: overrides.initialSortModel ?? base.defaultSortModel,
    initialFilterModel: overrides.initialFilterModel,
    persistKey: overrides.persistKey,
    defaultPersistKey: base.defaultPersistKey,
    syncStateToUrl: overrides.syncStateToUrl ?? false,
  });
